import React, { useState } from 'react';
import { TaxResult } from '../../types';
import { api } from '../../services/api';
import StoryCard from '../StoryCard';
import TabNavigation, { TabType } from '../TabNavigation';
import TaxSimulation from '../simulations/TaxSimulation';
import { modelStories } from '../../data/stories';
import { TrendingDown, Globe, Percent } from 'lucide-react';

interface TaxModuleProps {
  data: TaxResult | null;
  onOptimizationComplete: (data: TaxResult) => void;
  onLoadingStart: () => void;
}

const TaxModule: React.FC<TaxModuleProps> = ({ data, onOptimizationComplete, onLoadingStart }) => {
  const [activeTab, setActiveTab] = useState<TabType>(data ? 'run' : 'case-study');

  const handleOptimize = async () => {
    onLoadingStart();
    const result = await api.optimizeTax();
    onOptimizationComplete(result);
    setActiveTab('run');
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);

  return (
    <div className="space-y-6">
      <div className="section-card">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Tax Optimization</h2>
        <p className="text-gray-600 mb-4">
          Optimize global tax structure using Mixed-Integer Nonlinear Programming
        </p>
        
        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      {/* Case Study Tab - Always show the story */}
      {activeTab === 'case-study' && (
        <div className="section-card">
          <StoryCard story={modelStories.tax} />
        </div>
      )}

      {/* Run Tab */}
      {activeTab === 'run' && (
        <div className="space-y-6">
          <div className="section-card">
            <button type="button" onClick={handleOptimize} className="btn-primary">Optimize Tax Structure</button>
          </div>

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Current ETR</div>
              <div className="text-2xl font-bold text-red-600 flex items-center">
                <Percent className="w-5 h-5 mr-1" />
                {data.current_etr}%
              </div>
            </div>
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Optimized ETR</div>
              <div className="text-2xl font-bold text-green-600 flex items-center">
                <TrendingDown className="w-5 h-5 mr-1" />
                {data.optimized_etr}%
              </div>
            </div>
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Annual Savings</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(data.savings)}</div>
            </div>
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Countries</div>
              <div className="text-2xl font-bold text-gray-800 flex items-center">
                <Globe className="w-5 h-5 mr-1" />
                {data.allocations.length}
              </div>
            </div>
          </div>

          {/* Country Allocation Table */}
          <div className="table-container">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Optimized Profit Allocation by Country</h3>
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Country</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Profit Allocated</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Tax Rate</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Tax Amount</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">% of Total Tax</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.allocations.map((allocation, idx) => {
                  const totalTax = data.allocations.reduce((sum, a) => sum + a.tax, 0);
                  const taxPercentage = ((allocation.tax / totalTax) * 100).toFixed(1);
                  return (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{allocation.country}</td>
                      <td className="px-6 py-4 text-sm text-right text-gray-900">{formatCurrency(allocation.profit)}</td>
                      <td className="px-6 py-4 text-sm text-right">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          allocation.rate < 15 ? 'bg-green-100 text-green-800' :
                          allocation.rate < 25 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {allocation.rate}%
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-right font-semibold text-gray-900">{formatCurrency(allocation.tax)}</td>
                      <td className="px-6 py-4 text-sm text-right text-gray-600">{taxPercentage}%</td>
                    </tr>
                  );
                })}
                <tr className="bg-blue-50 font-semibold">
                  <td className="px-6 py-4 text-sm text-gray-900">Total</td>
                  <td className="px-6 py-4 text-sm text-right text-gray-900">
                    {formatCurrency(data.allocations.reduce((sum, a) => sum + a.profit, 0))}
                  </td>
                  <td className="px-6 py-4 text-sm text-right text-blue-700">{data.optimized_etr}%</td>
                  <td className="px-6 py-4 text-sm text-right text-blue-700">
                    {formatCurrency(data.allocations.reduce((sum, a) => sum + a.tax, 0))}
                  </td>
                  <td className="px-6 py-4 text-sm text-right text-gray-600">100%</td>
                </tr>
              </tbody>
            </table>
          </div>

          {data.llm_explanation && (
            <div className="section-card bg-purple-50 border-l-4 border-purple-500">
              <h3 className="text-lg font-bold text-purple-900 mb-3">AI Analysis</h3>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">{data.llm_explanation}</p>
            </div>
          )}

          {data.recommendations && (
            <div className="success-section">
              <h3 className="text-lg font-bold text-green-900 mb-3">Tax Strategy Recommendations</h3>
              <ol className="space-y-2">
                {data.recommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="font-bold text-green-700 mr-2">{idx + 1}.</span>
                    <span className="text-gray-700">{rec}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </>
      )}
        </div>
      )}

      {/* Simulation Tab */}
      {activeTab === 'simulation' && (
        <TaxSimulation />
      )}
    </div>
  );
};

export default TaxModule;
